import { type Channel, getChannelLabel, NATIVE_CHANNEL, SOCIAL_CHANNEL } from 'src/data/channel';
import { PROJECT_TYPE, type ProjectType } from 'src/data/project/const';
import { UniqMap } from 'src/lib/UniqMap';

import {
  AD_ASSET_TYPE,
  ASSET_TYPE,
  type AssetType,
  BLOG_POST_ASSET_TYPE,
  CANVAS_ASSET_TYPE,
  DEPRECATED_ASSET_TYPE,
  EDITOR_MARKED_ASSET_TYPE,
  EMAIL_ASSET_TYPE,
  IMAGE_ASSET_TYPE,
  LANDING_ASSET_TYPE,
  LONG_FORM_ASSET_TYPE,
  PRODUCT_LISTING_ASSET_TYPE,
  SMS_ASSET_TYPE,
} from './const';

/**
 * Asset props and utilities:
 */
interface AssetProps {
  label: string;
  channel?: Channel; // channel of the current asset type
  maxCopyLength?: number; // how long assets of this type might be
  strictCopyLengthCheck?: boolean; // should we enforce length check
  disableScores?: boolean; // should variation scores be hidden
  supportsSuggestKeywords?: boolean; // does asset type supports "suggest" keywords
  maxSuggestKeywords?: number;
  maxWordsPerKeyword?: number;
  supportsSuggestLink?: boolean; // does asset type supports "suggest" link
  supportsRunningAds?: boolean; // does asset type supports selection of "running" ads and tab
  supportsPublishing?: boolean;
  disableAnnotations?: boolean; // disable annotations for specific asset
}

const ASSET_PROPS_MAP = new UniqMap<AssetType, AssetProps>();

/* Common asset types props */
ASSET_PROPS_MAP.add(AD_ASSET_TYPE.facebookPrimaryText, {
  label: 'Primary text',
  channel: SOCIAL_CHANNEL.facebook,
  maxCopyLength: 125,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(AD_ASSET_TYPE.facebookHeadline, {
  label: 'Headline',
  channel: SOCIAL_CHANNEL.facebook,
  maxCopyLength: 40,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.facebookMixtureAd, {
  label: 'Meta Image Ad',
  channel: SOCIAL_CHANNEL.facebook,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.twitterMixtureAd, {
  label: 'X Ad',
  channel: SOCIAL_CHANNEL.twitter,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.pinterestMixtureAd, {
  label: 'Pinterest Ad',
  channel: SOCIAL_CHANNEL.pinterest,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinMixtureAd, {
  label: 'LinkedIn Single Image Ad',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinDocumentAd, {
  label: 'LinkedIn Document Ad',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinVideoAd, {
  label: 'LinkedIn Video Ad',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinTextAd, {
  label: 'LinkedIn Text Ad',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinMessageAd, {
  label: 'LinkedIn Message Ad',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinMessageAdSubject, {
  label: 'Subject',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 60,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinMessageAdMessage, {
  label: 'Message',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 1500,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinMessageAdCta, {
  label: 'CTA',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 20,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinConversationAd, {
  label: 'LinkedIn Conversation Ad',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinConversationalAdOpeningMessage, {
  label: 'Opening Message',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinConversationalAdPrimaryCtaButtonText, {
  label: 'Primary CTA Button Text',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 25,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 5,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinConversationalAdFollowUpMessage, {
  label: 'Follow Up Message',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinConversationalAdSecondaryCtaButtonText, {
  label: 'Secondary CTA Button Text',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 25,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinLeadGenFormAd, {
  label: 'LinkedIn Lead Gen Form Ad',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinLeadGenFormAdPrimaryText, {
  label: 'Primary Text',
  channel: SOCIAL_CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinLeadGenFormAdHeadline, {
  label: 'Headline',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 70,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinLeadGenFormAdOfferHeadline, {
  label: 'Offer Headline',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 60,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinLeadGenFormAdOfferDetails, {
  label: 'Offer Details',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 160,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleHeadline, {
  label: 'Headline',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 30,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleMediumHeadline, {
  label: 'Headline',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 40,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleShoppingTitle, {
  label: 'Title',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 150,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleShoppingDescription, {
  label: 'Description',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 500,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 1,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleLongHeadline, {
  label: 'Long headline',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 1,
  supportsRunningAds: true,
});

ASSET_PROPS_MAP.add(ASSET_TYPE.googleShortDescription, {
  label: 'Short description',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 60,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleDescription, {
  label: 'Description',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleRSA, {
  label: 'RSA',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleAppAd, {
  label: 'Google App Ad',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleDemandGenAd, {
  label: 'Google Demand Gen Ad',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleDisplayAd, {
  label: 'Google Demand Gen Ad',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googlePerformanceMaxAd, {
  label: 'PMAX',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleShoppingAd, {
  label: 'Google Shopping Ad',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.googleVideoAd, {
  label: 'Google Video Ad',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.outbrainAd, {
  label: 'Outbrain Ad',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.taboolaAd, {
  label: 'Taboola Ad',
  channel: SOCIAL_CHANNEL.google,
  maxCopyLength: 90,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsRunningAds: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinTextAdHeadline, {
  label: 'Headline',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 25,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinTextAdDescription, {
  label: 'Intro text',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 75,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinIntro, {
  label: 'Intro text',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 150,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinTitle, {
  label: 'Headline',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 70,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinSingleImageHeadline, {
  label: 'Headline',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 27,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinSingleImageIntro, {
  label: 'Introductory text',
  channel: SOCIAL_CHANNEL.linkedin,
  maxCopyLength: 150,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.twitterTitle, {
  label: 'Headline',
  channel: SOCIAL_CHANNEL.twitter,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.twitterCopy, {
  label: 'Copy',
  channel: SOCIAL_CHANNEL.twitter,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.pinterestTitle, {
  label: 'Title',
  channel: SOCIAL_CHANNEL.pinterest,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.pinterestDescription, {
  label: 'Description',
  channel: SOCIAL_CHANNEL.pinterest,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.youtubeTitle, {
  label: 'Title',
  channel: SOCIAL_CHANNEL.youtube,
  maxCopyLength: 70,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.youtubeDescription, {
  label: 'Description',
  channel: SOCIAL_CHANNEL.youtube,
  maxCopyLength: 750,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.outbrainHeadlineProduct, {
  label: 'Title',
  channel: NATIVE_CHANNEL.outbrain,
  maxCopyLength: 100,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.outbrainHeadline, {
  label: 'Title',
  channel: NATIVE_CHANNEL.outbrain,
  maxCopyLength: 100,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.outbrainDescriptionProduct, {
  label: 'Description',
  channel: NATIVE_CHANNEL.outbrain,
  maxCopyLength: 150,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.outbrainDescription, {
  label: 'Description',
  channel: NATIVE_CHANNEL.outbrain,
  maxCopyLength: 150,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.taboolaHeadline, {
  label: 'Title',
  channel: NATIVE_CHANNEL.taboola,
  maxCopyLength: 60,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.taboolaDescription, {
  label: 'Description',
  channel: NATIVE_CHANNEL.taboola,
  maxCopyLength: 260,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.verizonHeadline, {
  label: 'Headline',
  channel: NATIVE_CHANNEL.verizon,
  maxCopyLength: 50,
  strictCopyLengthCheck: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.verizonDescription, {
  label: 'Description',
  channel: NATIVE_CHANNEL.verizon,
  maxCopyLength: 255,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});

/* Common assets for "Teads" channel  */
ASSET_PROPS_MAP.add(ASSET_TYPE.teadsSingleImageHeadline, {
  label: 'Headline',
  channel: NATIVE_CHANNEL.teads,
  maxCopyLength: 50,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.teadsSingleImageDescription, {
  label: 'Description',
  channel: NATIVE_CHANNEL.teads,
  maxCopyLength: 140,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.teadsSingleVideoHeadline, {
  ...ASSET_PROPS_MAP.get(ASSET_TYPE.teadsSingleImageHeadline),
  label: 'Headline',
});
ASSET_PROPS_MAP.add(ASSET_TYPE.teadsSingleVideoDescription, {
  ...ASSET_PROPS_MAP.get(ASSET_TYPE.teadsSingleImageDescription),
  label: 'Description',
});
ASSET_PROPS_MAP.add(ASSET_TYPE.teadsCarouselHeadline, {
  ...ASSET_PROPS_MAP.get(ASSET_TYPE.teadsSingleImageHeadline),
  label: 'Carousel',
});
ASSET_PROPS_MAP.add(ASSET_TYPE.teadsSocialLayout1Headline, {
  label: 'Headline',
  channel: NATIVE_CHANNEL.teads,
  maxCopyLength: 40,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.teadsSocialLayout1PrimaryText, {
  label: 'Primary text',
  channel: NATIVE_CHANNEL.teads,
  maxCopyLength: 125,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.teadsSocialLayout1Description, {
  label: 'Description',
  channel: NATIVE_CHANNEL.teads,
  maxCopyLength: 25,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.teadsSocialLayout2PrimaryText, {
  ...ASSET_PROPS_MAP.get(ASSET_TYPE.teadsSocialLayout1PrimaryText),
  label: 'Primary text',
});

/* Social asset types */
ASSET_PROPS_MAP.add(ASSET_TYPE.facebookPost, {
  label: 'Text',
  channel: SOCIAL_CHANNEL.facebook,
  maxCopyLength: 150,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.instagramCaption, {
  label: 'Caption',
  channel: SOCIAL_CHANNEL.instagram,
  maxCopyLength: 70,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.tweet, {
  label: 'Text',
  channel: SOCIAL_CHANNEL.twitter,
  maxCopyLength: 280,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.linkedinPost, {
  label: 'Text',
  channel: SOCIAL_CHANNEL.linkedinSocial,
  maxCopyLength: 1600,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.pinterestMixturePost, {
  label: 'Pinterest Post',
  channel: SOCIAL_CHANNEL.pinterest,
  maxCopyLength: 500,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.youtubeMixturePost, {
  label: 'YouTube Post',
  channel: SOCIAL_CHANNEL.youtube,
  maxCopyLength: 750,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.pinterestPostTitle, {
  label: 'Title',
  channel: SOCIAL_CHANNEL.pinterest,
  maxCopyLength: 100,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});
ASSET_PROPS_MAP.add(ASSET_TYPE.pinterestPost, {
  label: 'Description',
  channel: SOCIAL_CHANNEL.pinterest,
  maxCopyLength: 500,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
});

/* Product listing asset types props */
ASSET_PROPS_MAP.add(PRODUCT_LISTING_ASSET_TYPE.amazonProductDescription, {
  label: 'Paragraph description',
  channel: NATIVE_CHANNEL.amazon,
  maxCopyLength: 500,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  strictCopyLengthCheck: true,
});
ASSET_PROPS_MAP.add(PRODUCT_LISTING_ASSET_TYPE.amazonFeatureList, {
  label: 'Feature list',
  channel: NATIVE_CHANNEL.amazon,
  maxCopyLength: 850,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  strictCopyLengthCheck: true,
});
ASSET_PROPS_MAP.add(PRODUCT_LISTING_ASSET_TYPE.walmartProductDescription, {
  label: 'Paragraph description',
  channel: NATIVE_CHANNEL.walmart,
  maxCopyLength: 500,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  strictCopyLengthCheck: true,
});
ASSET_PROPS_MAP.add(PRODUCT_LISTING_ASSET_TYPE.walmartFeatureList, {
  label: 'Feature list',
  channel: NATIVE_CHANNEL.walmart,
  maxCopyLength: 850,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  strictCopyLengthCheck: true,
});
ASSET_PROPS_MAP.add(PRODUCT_LISTING_ASSET_TYPE.generalProductDescription, {
  label: 'Paragraph description',
});
ASSET_PROPS_MAP.add(PRODUCT_LISTING_ASSET_TYPE.generalProductListing, {
  label: 'Text',
});
ASSET_PROPS_MAP.add(PRODUCT_LISTING_ASSET_TYPE.amazonProductListing, {
  label: 'Text',
  channel: NATIVE_CHANNEL.amazon,
});
ASSET_PROPS_MAP.add(PRODUCT_LISTING_ASSET_TYPE.walmartProductListing, {
  label: 'Text',
  channel: NATIVE_CHANNEL.walmart,
});

/**
 * LPCO asset types
 */
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingCTA, {
  label: 'Call to action',
  maxCopyLength: 100,
  supportsSuggestKeywords: true,
  strictCopyLengthCheck: true,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingCopy, {
  label: 'Copy',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxCopyLength: 200,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingProductName, {
  label: 'Product name',
  maxCopyLength: 150,
  supportsSuggestKeywords: true,
  strictCopyLengthCheck: true,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingSEOMetadata, {
  label: 'SEO meta description',
  maxCopyLength: 160,
  strictCopyLengthCheck: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingSEOMetaTitle, {
  label: 'SEO meta title',
});

/**
 * Shopify LPCO asset types
 */
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingHeadline, {
  label: 'Headline',
  maxCopyLength: 150,
  supportsSuggestKeywords: true,
  strictCopyLengthCheck: true,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingProductDescription, {
  label: 'Paragraph description',
  maxCopyLength: 850,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  strictCopyLengthCheck: true,
});

/**
 * "Landing page builder" assets
 */
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingBuilderHeadline, {
  label: 'Headline',
  maxCopyLength: 150,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingBuilderSubHeadline, {
  label: 'Subheadline',
  maxCopyLength: 150,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingBuilderCta, {
  label: 'Call to action',
  maxCopyLength: 150,
  maxSuggestKeywords: 2,
  disableAnnotations: true,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.reinforcementStatement, {
  label: 'Reinforcement statement',
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.benefits, {
  label: 'Benefits',
  maxCopyLength: 150,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.features, {
  label: 'Features',
  maxCopyLength: 150,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
ASSET_PROPS_MAP.add(LANDING_ASSET_TYPE.landingSectionHeadline, {
  label: 'Section headline',
});

/* Email asset types props */
ASSET_PROPS_MAP.add(EMAIL_ASSET_TYPE.emailMixture, {
  label: 'Email',
  maxSuggestKeywords: 1,
  supportsSuggestKeywords: true,
  supportsPublishing: true,
});

ASSET_PROPS_MAP.add(EMAIL_ASSET_TYPE.emailSubject, {
  label: 'Subject line',
  maxCopyLength: 40,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsPublishing: true,
});

ASSET_PROPS_MAP.add(EMAIL_ASSET_TYPE.emailBody, {
  label: 'Body',
  maxCopyLength: 600,
  maxSuggestKeywords: 1,
  disableScores: true,
  supportsPublishing: true,
});

ASSET_PROPS_MAP.add(EMAIL_ASSET_TYPE.emailColdOutreachBody, {
  label: 'Cold Body',
  maxCopyLength: 300,
  supportsPublishing: true,
});
ASSET_PROPS_MAP.add(EMAIL_ASSET_TYPE.emailPromotionalBody, {
  label: 'Promotional Body',
  maxCopyLength: 500,
  supportsPublishing: true,
});

/* Blog post asset types props */
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.blogPostOutline, {
  label: 'Outline',
  disableScores: true,
  disableAnnotations: true,
});
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.blogPostTitle, {
  label: 'Title',
  maxCopyLength: 80,
  disableAnnotations: true,
});
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.blogPostIntroParagraph, {
  label: 'Intro paragraph',
  maxCopyLength: 600,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  disableAnnotations: true,
});
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.blogPostContent, {
  label: 'Content',
});
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.blogPostList, {
  label: 'List',
});
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.blogPostFacts, {
  label: 'Facts',
});
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.blogPostHeadline, {
  label: 'Headline',
});
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.blogPostSectionHeadline, {
  label: 'Section headline',
});
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.blogPostSectionSubHeadline, {
  label: 'Section subheadline',
});
ASSET_PROPS_MAP.add(BLOG_POST_ASSET_TYPE.introParagraph, {
  label: 'Intro paragraph',
});
ASSET_PROPS_MAP.add(EMAIL_ASSET_TYPE.emailHeadline, {
  label: 'Headline',
});
ASSET_PROPS_MAP.add(EMAIL_ASSET_TYPE.emailCta, {
  label: 'Call to action',
});
ASSET_PROPS_MAP.add(EMAIL_ASSET_TYPE.emailIntroParagraph, {
  label: 'Introduction',
});
ASSET_PROPS_MAP.add(EMAIL_ASSET_TYPE.emailSectionHeadline, {
  label: 'Section headline',
});

/* SMS asset types props */
ASSET_PROPS_MAP.add(SMS_ASSET_TYPE.sms, {
  label: 'Promotional SMS',
  maxCopyLength: 480,
  strictCopyLengthCheck: true,
  supportsSuggestLink: true,
});

/* Canvas asset types props */
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasContentImprover, {
  label: 'General Text',
  maxCopyLength: 4800,
  strictCopyLengthCheck: true,
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasEli5, {
  label: 'Explain it to a 5th grader',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasSentenceExpander, {
  label: 'Sentence expander',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasAida, {
  label: 'AIDA',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasPas, {
  label: 'PAS',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasBab, {
  label: 'BAB',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasPerfectHeadline, {
  label: 'Perfect headline',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasPersuasiveBulletPoint, {
  label: 'Powerful bullet points',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasFeatureToBenefitsPractical, {
  label: 'Features to benefits',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasValueProposition, {
  label: 'Value proposition',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasProductDescriptionList, {
  label: 'Product description — list',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasProductDescriptionRelaxed, {
  label: 'Product description — paragraph',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasBlogIdeas, {
  label: 'Blog post topic ideas',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasVideoIdeas, {
  label: 'Video topic ideas',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.canvasFreestyle, {
  label: 'General text',
  maxCopyLength: 3000,
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.productReview, {
  label: 'Text',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.freeTextHeadline, {
  label: 'Headline',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.freeTextTopic, {
  label: 'Keywords',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.freeTextBody, {
  label: 'Body',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.personalBio, {
  label: 'Personal bio',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.companyBio, {
  label: 'Company bio',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.engagingQuestions, {
  label: 'Engaging questions',
});
ASSET_PROPS_MAP.add(CANVAS_ASSET_TYPE.quoraAnswers, {
  label: 'Quora answers',
});

ASSET_PROPS_MAP.add(EDITOR_MARKED_ASSET_TYPE.editorParagraph, {
  label: 'Editor paragraph',
});
ASSET_PROPS_MAP.add(EDITOR_MARKED_ASSET_TYPE.editorRephrase, {
  label: 'Editor rephrase',
});

/* Long form asset types props */
ASSET_PROPS_MAP.add(LONG_FORM_ASSET_TYPE.longFormSectionHeadline, {
  label: 'General writing block headline',
});
ASSET_PROPS_MAP.add(LONG_FORM_ASSET_TYPE.longFormTitle, {
  label: 'Title',
});

/**
 * Deprecated asset types props
 */
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.subHeading, {
  label: 'Sub heading (deprecated)',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.subHeadingSupportingCopy, {
  label: 'Supporting copy (deprecated)',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.consumerBenefits, {
  label: 'Product description',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasFacebookAd, {
  label: 'Facebook ad',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasGoogleAd, {
  label: 'Google ad',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasTwitterAd, {
  label: 'X ad',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasLinkedinTextAd, {
  label: 'Linkedin text ad',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasPinterestAd, {
  label: 'Pinterest ad',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasLandingPage, {
  label: 'Landing page copy',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasLandingPageSeo, {
  label: 'SEO meta description',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasFacebookSocial, {
  label: 'Facebook post',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasInstagramSocial, {
  label: 'Instagram caption',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasLinkedinSocial, {
  label: 'LinkedIn post',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasTwitterSocial, {
  label: 'X post',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasYoutubeSocial, {
  label: 'YouTube description',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasPinterestPinSocial, {
  label: 'Pinterest pin',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasColdEmail, {
  label: 'Cold email',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasPromotionalEmail, {
  label: 'Promotional email',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasEmailSubjectLine, {
  label: 'Email subject line',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasSms, {
  label: 'Promotional SMS',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasAmazonProductDescription, {
  label: 'Amazon listing - paragraph',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasAmazonFeatureList, {
  label: 'Amazon listing - feature list',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasWalmartProductDescription, {
  label: 'Walmart listing - paragraph',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.canvasWalmartFeatureList, {
  label: 'Walmart listing - feature list',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.emailSubjectLine, {
  label: 'Subject line',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.emailPromotionalSubjectLine, {
  label: 'Subject line',
});
ASSET_PROPS_MAP.add(DEPRECATED_ASSET_TYPE.emailColdOutreachSubjectLine, {
  label: 'Subject line',
});
ASSET_PROPS_MAP.add(IMAGE_ASSET_TYPE.imageWithDescription, {
  label: 'Image',
});
ASSET_PROPS_MAP.add(IMAGE_ASSET_TYPE.image, {
  label: 'Image',
});

const getAssetProp = <T>(
  type: AssetType,
  propName: keyof AssetProps,
  defaultValue?: T
): T | undefined => {
  const props = ASSET_PROPS_MAP.get(type);
  if (props && props[propName] != null) {
    return props[propName] as T;
  }
  return defaultValue;
};

export const getAssetChannel = (assetType: AssetType): Channel | undefined =>
  getAssetProp<Channel>(assetType, 'channel');

export const getAssetLabel = (assetType: AssetType): string =>
  getAssetProp<string>(assetType, 'label') || '';

export const getAssetTypeMaxSuggestKeywords = (assetType: AssetType) =>
  getAssetProp<number>(assetType, 'maxSuggestKeywords') || 1;

export const getAssetTypeMaxWordsPerKeyword = (assetType: AssetType) =>
  getAssetProp<number>(assetType, 'maxWordsPerKeyword') || 3;

export const isAssetScoresDisabled = (assetType: AssetType) =>
  getAssetProp<boolean>(assetType, 'disableScores') || false;

export const isAssetSupportSuggestKeywords = (assetType: AssetType) =>
  getAssetProp<boolean>(assetType, 'supportsSuggestKeywords') || false;

export const isAssetSupportSuggestLink = (assetType: AssetType) =>
  getAssetProp<boolean>(assetType, 'supportsSuggestLink') || false;

export const isAssetDisabledAnnotations = (assetType: AssetType) =>
  getAssetProp<boolean>(assetType, 'disableAnnotations') || false;

export const getAssetTypeMaxCopyLength = (assetType: AssetType) =>
  getAssetProp<number>(assetType, 'maxCopyLength') || 0;

export const shouldAssetCheckCopyLength = (assetType: AssetType) =>
  getAssetProp<boolean>(assetType, 'strictCopyLengthCheck') || false;

export const doesAssetSupportPublishing = (assetType: AssetType) =>
  getAssetProp<boolean>(assetType, 'supportsPublishing') || false;

const createOption = (assetType: AssetType) => ({
  value: assetType,
  label: getAssetLabel(assetType),
});

export const getAssetTypeForProject = (projectType: ProjectType) => {
  switch (projectType) {
    case PROJECT_TYPE.continuousOptimization:
    default:
      return [
        createOption(LANDING_ASSET_TYPE.landingHeadline),
        createOption(LANDING_ASSET_TYPE.landingCopy),
        createOption(LANDING_ASSET_TYPE.landingCTA),
        createOption(LANDING_ASSET_TYPE.landingProductName),
      ];
  }
};

export const getAssetChannelLabel = (assetType: AssetType) => {
  const channel = getAssetChannel(assetType);
  return channel ? getChannelLabel(channel) : '';
};
